/**
 * GIVE / TAKE tab — README §1: two side-by-side panels (38px square Resume avatars, the
 * offer / ask, name + meta, footer button adds a row) via GiveTakeBoard's `surface` variant,
 * then the black MAKE IT MORE EPIC strip: the guest-facing view of a MIME round, rendering the
 * viewer's pending MimeRequestCards (COMMIT / PASS). MIME requests render here and nowhere else.
 */
import React from 'react';
import { Panel, PanelHeader, KVLine, EmptyBox, SurfaceButton, SUBTLE, GREEN, SOFT } from '../surface';
import { money } from '../../utils/surfaceFormat';
import GiveTakeBoard from '../GiveTakeBoard';
import MimeRequestCard from '../MimeRequestCard';

/**
 * YOUR COMMITMENT — original pledge + every accepted MIME round + total (and the refund
 * line once a declined application has been paid back). Only when there is something
 * beyond the base pledge to explain, i.e. at least one accepted round.
 */
export function CommitmentBreakdown({ application, history = [], mobile = false }) {
  const accepted = history.filter((r) => r.status === 'Accepted');
  if (!application || accepted.length === 0) return null;
  const base = application.pledgeAmountCents || 0;
  const total = application.partyCreditsCommitted || base;
  return (
    <Panel data-testid="commitment-breakdown">
      <PanelHeader label="YOUR COMMITMENT" right={`${accepted.length} ${accepted.length === 1 ? 'ROUND' : 'ROUNDS'} IN`} dense={mobile} />
      <div className="flex flex-col gap-2" style={{ padding: 14, background: SOFT.canvas, borderRadius: 18 }}>
        <KVLine k="Original pledge" v={money(base)} />
        {accepted.map((r, i) => <KVLine key={r.id || i} k={`MIME round ${i + 1}`} v={money(r.committedAmountCents || r.additionalCommitmentCents || 0, { signed: true })} />)}
        <div style={{ paddingTop: 8, borderTop: '2px solid #000' }}><KVLine k="TOTAL COMMITTED" v={money(total)} size={14} /></div>
        {application.status === 'declined' && application.partyCreditsRefunded && <KVLine k="REFUNDED" v={money(total, { signed: true })} color={GREEN} />}
      </div>
    </Panel>
  );
}

export function MimeStrip({ requests = [], onAccept, onDecline, respondingId, errors = {}, mobile = false }) {
  if (requests.length === 0) return null;
  return (
    <Panel black dense={mobile} style={{ borderRadius: 20 }} data-testid="mime-strip">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.16em' }}>MAKE IT MORE EPIC</div>
          <div className="font-mono" style={{ fontSize: 12, color: SUBTLE, marginTop: 4 }}>
            The host is asking everyone for a little more. {requests.length === 1 ? 'One ask' : `${requests.length} asks`} on the clock.
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 bg-white text-black" style={{ padding: mobile ? 12 : 16, borderRadius: 14 }}>
        {requests.map((req) => (
          <MimeRequestCard
            key={req.id}
            request={req}
            onAccept={onAccept}
            onDecline={onDecline}
            isResponding={respondingId === req.id}
            error={errors[req.id] || null}
            declineLabel="PASS"
          />
        ))}
      </div>
    </Panel>
  );
}

export default function EventBoardTab({
  eventId,
  giveTakeEnabled = true,
  isAuthenticated,
  mobile = false,
  onSignIn,
  mimeRequests = [],
  mimeHistory = [],
  application = null,
  onMimeAccept,
  onMimeDecline,
  respondingMimeId,
  mimeErrors,
}) {
  const mime = (
    <>
      <MimeStrip requests={mimeRequests} onAccept={onMimeAccept} onDecline={onMimeDecline} respondingId={respondingMimeId} errors={mimeErrors} mobile={mobile} />
      <CommitmentBreakdown application={application} history={mimeHistory} mobile={mobile} />
    </>
  );
  if (!isAuthenticated) {
    return (
      <EmptyBox title="SIGN IN TO GIVE OR TAKE." mobile={mobile} testId="board-signin" action={<SurfaceButton variant="fill" size="sm" onClick={onSignIn}>SIGN IN</SurfaceButton>}>
        What people are offering, and what they need. Claim one and you sort the rest in a private chat.
      </EmptyBox>
    );
  }
  // Board off: no board and no "it's off" notice either — the tab itself is gone
  // (`eventTabs`), and this only renders at all because a MIME round brought it back.
  return (
    <div className="flex flex-col" style={{ gap: mobile ? 10 : 14 }} data-testid="board-tab">
      {giveTakeEnabled !== false && <GiveTakeBoard context="event" eventId={eventId} variant="surface" mobile={mobile} />}
      {mime}
    </div>
  );
}
